import { i18n } from 'lib/i18n'
import { clearStringAddress, PlaceE, PlaceS, PlaceV0 } from 'lib/place'
import {
  DiscoverResultItem,
  MediaEditorialsItem,
  MediaImagesItem,
  MediaRatingsItem,
  SearchResultEntityPlaceItemContacts,
} from 'map/services'
import { calculateAndFormatDistance, CountryAndUnit } from 'utils/formatters'

import { Coordinates } from './index'
import { SearchStateListItem } from './search'

export interface PdcState {
  pdcItem?: PdcStateItem
  isLoading: boolean
  error?: string
}

// TODO Need to resolve the types here and make more specific state oriented types
// At the moment there are types are coming from map/service and those are part of state properties.
// The problem with this approach is that when a state is consumed by the components will create a coupling with map/service just to read the data,
// which is not idiomatic
export interface PdcStateItem {
  id?: string
  title: string
  distanceLabel?: string
  thumbnail?: string
  averageRating?: number
  ratingLink?: string
  reviewCount?: number
  addressLabel?: string
  primaryCategory?: {
    id: string
    name: string
    primary?: boolean
  }
  categories?: {
    id: string
    name: string
    primary?: true
  }[]
  contacts?: PdcStateItemContact
  openingHours?: PdcStateItemOpeningHours
  mapView?: {
    west: number
    south: number
    east: number
    north: number
  }
  media?: {
    images?: {
      items: MediaImagesItem[]
    }
    ratings?: {
      items: MediaRatingsItem[]
    }
    editorials?: {
      items: MediaEditorialsItem[]
    }
  }
  distance?: number
  position?: Position
  resultType?: string
}

export interface Position {
  lat: number
  lng: number
}

interface PdcStateItemContact {
  email?: string
  phone?: string
  www?: string
}

interface PdcStateItemOpeningHours {
  isOpen: boolean
  status: string
  weekSchedule: {
    timings: string
    weekDay: string
  }[]
}
function getContactItem(
  contacts: SearchResultEntityPlaceItemContacts[],
  primaryCategoryId: string | undefined,
  key: 'email' | 'phone' | 'www'
): string | undefined {
  const defaultVal = contacts[0][key]?.[0]?.value
  if (primaryCategoryId === null || primaryCategoryId === undefined) return defaultVal

  let value: string | undefined = undefined
  const found = contacts.find((contact) => {
    value = contact[key]?.find((item) =>
      item.categories?.find((category) => category.id === primaryCategoryId)
    )?.value
    return value
  })

  if (found) return value

  return defaultVal
}

function getContacts(
  item: DiscoverResultItem,
  primaryCategoryId: string | undefined
): PdcStateItemContact | undefined {
  if ('contacts' in item && item.contacts !== undefined && item.contacts.length > 0) {
    const www = getContactItem(item.contacts, primaryCategoryId, 'www')
    return {
      email: getContactItem(item.contacts, primaryCategoryId, 'email'),
      phone: getContactItem(item.contacts, primaryCategoryId, 'phone'),
      www: www?.includes('http') === true ? www : undefined,
    }
  }
  return undefined
}

function getOpeningHours(
  item: DiscoverResultItem,
  primaryCategoryId: string | undefined
): PdcStateItemOpeningHours | undefined {
  if ('openingHours' in item && item.openingHours !== undefined && item.openingHours.length > 0) {
    const openingHours =
      primaryCategoryId === null || primaryCategoryId === undefined
        ? item.openingHours[0]
        : item.openingHours.find((item) =>
            item.categories?.find((category) => category.id === primaryCategoryId)
          ) ?? item.openingHours[0]

    return {
      isOpen: openingHours.isOpen,
      status: openingHours.isOpen ? i18n(`status.open`) : i18n(`status.closed`),
      weekSchedule: openingHours.text.map((data) => {
        const dayTimings = data.split(':')
        const weekDay = dayTimings.length > 0 ? (dayTimings.shift() as string) : ''
        const timings = dayTimings.join(':').trim()
        return {
          timings,
          weekDay,
        }
      }),
    }
  }
  return undefined
}

const fromDiscoverResultItemToPdcItem = (
  item: DiscoverResultItem,
  countryAndUnit: CountryAndUnit,
  userCurrentPosition?: Coordinates | null
): PdcStateItem => {
  const id = item.id
  const primaryCategory =
    'categories' in item ? item.categories.filter(({ primary }) => primary)[0] : undefined
  const distanceLabel =
    'position' in item && userCurrentPosition !== null && userCurrentPosition !== undefined
      ? calculateAndFormatDistance(item.position, userCurrentPosition, countryAndUnit)
      : undefined
  const thumbnail =
    'media' in item && item.media?.images?.items?.length !== undefined
      ? item.media.images.items[0].href
      : undefined
  const media = 'media' in item ? item.media : undefined
  const contacts = getContacts(item, primaryCategory?.id)

  const { averageRating, reviewCount, ratingLink } =
    'media' in item && item.media?.ratings?.items?.length !== undefined
      ? {
          averageRating: item.media.ratings.items[0].average,
          ratingLink: item.media.ratings.items[0].href,
          reviewCount: item.media.ratings.items[0].count,
        }
      : { averageRating: undefined, ratingLink: undefined, reviewCount: undefined }
  const categories = 'categories' in item ? item.categories : undefined
  const position = 'position' in item ? item.position : undefined
  const distance = 'distance' in item ? item.distance : undefined
  const openingHours = getOpeningHours(item, primaryCategory?.id)
  const title = item.title
  const addressLabel = 'address' in item ? clearStringAddress(item.address.label, title) : undefined
  const resultType = item.resultType
  const mapView = 'mapView' in item ? item.mapView : undefined

  return {
    addressLabel,
    averageRating,
    categories,
    contacts,
    distance,
    distanceLabel,
    id,
    mapView,
    media,
    openingHours,
    position,
    primaryCategory,
    ratingLink,
    resultType,
    reviewCount,
    thumbnail,
    title,
  }
}

const fromSearchStateListItemToPdcItem = (item: SearchStateListItem): PdcStateItem => {
  const id = item.id
  const title = item.title
  const addressLabel =
    'address' in item
      ? item.address !== undefined
        ? clearStringAddress(item.address.label, title)
        : undefined
      : undefined
  const averageRating = item.averageRating

  const categories = item.categories
  const contacts =
    'contacts' in item && item.contacts !== undefined && item.contacts.length > 0
      ? {
          email: item.contacts[0].email?.[0]?.value,
          phone: item.contacts[0].phone?.[0]?.value,
          www: item.contacts[0].www?.[0]?.value,
        }
      : undefined
  const distance = item.distance
  const distanceLabel = item.distanceLabel
  const media = item.media
  const openingHours =
    'openingHours' in item && item.openingHours !== undefined && item.openingHours.length > 0
      ? {
          isOpen: item.openingHours[0].isOpen,
          status: item.openingHours[0].isOpen ? i18n(`status.open`) : i18n(`status.closed`),
          weekSchedule: item.openingHours[0].text.map((data) => {
            const dayTimings = data.split(':')
            const weekDay = dayTimings.length > 0 ? (dayTimings.shift() as string) : ''
            const timings = dayTimings.join(':').trim()
            return {
              timings,
              weekDay,
            }
          }),
        }
      : undefined

  const position = item.position
  const primaryCategory =
    'categories' in item ? item.categories?.filter(({ primary }) => primary)[0] : undefined
  const reviewCount = item.reviewCount
  const thumbnail = item.thumbnail

  return {
    addressLabel,
    averageRating,
    categories,
    contacts,
    distance,
    distanceLabel,
    id,
    media,
    openingHours,
    position,
    primaryCategory,
    reviewCount,
    thumbnail,
    title,
  }
}

export enum MappingType {
  DiscoverResultItem,
  SearchStateListItem,
  PlaceV0,
  PlaceS,
  PlaceE,
}

const fromPlaceV0ToPdcItem = (placeV0: PlaceV0): PdcStateItem => {
  const title = placeV0.title.replace(',', '')
  return {
    addressLabel: clearStringAddress(placeV0.address, title),
    id: placeV0.id,
    position: placeV0.position,
    title: title,
  }
}

const fromPlaceSToPdcItem = (placeS: PlaceS): PdcStateItem => {
  const contacts =
    placeS.ph !== undefined
      ? {
          phone: placeS.ph,
        }
      : undefined
  return {
    contacts,
    id: placeS.id,
    position: {
      lat: placeS.lat,
      lng: placeS.lon,
    },
    title: placeS.n,
  }
}

// TODO this version should be not supported by the pdc,
// although this workaround is caused because mobile create those serializations with data
// that external id semantically never represents
const fromPlaceEToPdcItem = (placeE: PlaceE): PdcStateItem => {
  return {
    id: placeE.providerId,
    position: {
      lat: placeE.latitude,
      lng: placeE.longitude,
    },
    title: '',
  }
}

export const toPdcItem = (
  from: MappingType,
  item: unknown,
  countryAndUnit: CountryAndUnit,
  userCurrentPosition?: Coordinates | null
): PdcStateItem => {
  switch (from) {
    case MappingType.DiscoverResultItem:
      return fromDiscoverResultItemToPdcItem(
        item as DiscoverResultItem,
        countryAndUnit,
        userCurrentPosition
      )
    case MappingType.SearchStateListItem:
      return fromSearchStateListItemToPdcItem(item as SearchStateListItem)
    case MappingType.PlaceV0:
      return fromPlaceV0ToPdcItem(item as PlaceV0)
    case MappingType.PlaceS:
      return fromPlaceSToPdcItem(item as PlaceS)
    case MappingType.PlaceE:
      return fromPlaceEToPdcItem(item as PlaceE)
  }
}
